/**
 * Settings hub.
 *
 * Backup sits high on this page on purpose. An installed PWA's storage is still
 * evictable — `navigator.storage.persist()` is a hint the browser may ignore —
 * so an export is the only copy of an unsynced session that survives the
 * browser reclaiming space. Burying that behind an "advanced" disclosure would
 * be the app being polite about the one thing that loses people's data.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { getTheme, setTheme, useSyncState } from "../../main.tsx";
import type { ScreenProps, Theme } from "../../main.tsx";
import { setLocale, useLocale } from "../../locale.ts";
import { LOCALES, LOCALE_NAMES, UNREVIEWED, type Locale } from "../../../shared/catalog.ts";
import { signOut, useAuthSession } from "../../auth/session.ts";
import type { AuthUser } from "../../auth/session.ts";
import { Tile } from "../../components/Tile.tsx";
import type { Aspect, PlaySession, Profile } from "../../../shared/types.ts";
import { exportBlob, importJson, lastBackupAgo, wipeLocal } from "../../store/sync.ts";
import { useLocal } from "./data.ts";
import {
  WIPE_PHRASE,
  backupFilename,
  backupStatus,
  formatAgo,
  isArchived,
  wipeConsequences,
} from "./logic.ts";
import { Banner, ConfirmButton, Note, Section, SettingsScreen } from "./ui.tsx";

import "./settings.css";

const THEMES: readonly { value: Theme; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "System" },
];

/**
 * Ordered as `LOCALES` is, so the segmented control and the catalogue agree.
 *
 * Each language is named in itself — a reader who has landed in the wrong one
 * has to be able to find their way out, and "German" does not help somebody who
 * only reads German.
 */
const LANGUAGE_OPTIONS: readonly { value: Locale; label: string; unreviewed: boolean }[] =
  LOCALES.map((value) => ({
    value,
    label: LOCALE_NAMES[value],
    unreviewed: UNREVIEWED.includes(value),
  }));

function useBackupAge(): { ago: number | null; refresh: () => void } {
  const [ago, setAgo] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let live = true;
    void lastBackupAgo()
      .then((value) => {
        if (live) setAgo(value);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [nonce]);
  return { ago, refresh: () => setNonce((n) => n + 1) };
}

/** Reported as it is: "persisted" is a request the browser granted, not a promise. */
function usePersistence(): boolean | null {
  const [persisted, setPersisted] = useState<boolean | null>(null);
  useEffect(() => {
    const probe = navigator.storage?.persisted?.();
    if (probe) void probe.then(setPersisted).catch(() => setPersisted(null));
  }, []);
  return persisted;
}

/**
 * What signing out costs, said before it happens.
 *
 * The queue is the part nobody expects. `signOut` deliberately leaves the local
 * rows and the outbox alone, so unsent work survives the sign-out — right up
 * until a *different* account signs in on this device, which wipes the store
 * (see `adoptAccount`). Someone handing the phone across the table deserves to
 * hear that while there is still time to wait for a sync or export.
 */
export function signOutConsequences(unsynced: number): readonly string[] {
  const lines = [
    "This device goes back to the sign-in screen.",
    "Nothing local is erased. Signing back in with the same account brings this device back as it is.",
  ];
  if (unsynced > 0) {
    const plural = unsynced === 1 ? "1 change has" : `${unsynced} changes have`;
    lines.push(
      `${plural} not reached the table yet. They are kept and go out when you sign back in — but if someone else signs in here first, they are wiped along with the rest of the local data.`,
    );
  }
  return lines;
}

/**
 * Who this device belongs to, and the way out of it.
 *
 * Deliberately free of hooks and given its user rather than reading the store:
 * that is what lets it be called as a plain function and its sign-out path
 * exercised without a renderer, the same way the shell's components are tested.
 *
 * Signing out runs the store's own `signOut` — the same path an expired session
 * takes — so the app leaves through one door rather than two.
 */
export function AccountCard({ user, unsynced }: { user: AuthUser; unsynced: number }): ReactNode {
  return (
    <Section
      title="Account"
      hint="Punchboard is per-account, and a phone at a table gets passed around. This is the only place that says whose it is."
    >
      <div className="pb-account">
        <span className="pb-label">Signed in as</span>
        <span className="pb-account__name">{user.name}</span>
        <span className="pb-account__email">{user.email}</span>
      </div>

      <ConfirmButton
        label="Sign out"
        title="Sign out of Punchboard"
        consequences={signOutConsequences(unsynced)}
        confirmLabel="Sign out of this device"
        onConfirm={async () => {
          await signOut();
        }}
      />
    </Section>
  );
}

export function Settings(_props: ScreenProps): ReactNode {
  const sync = useSyncState();
  const { user } = useAuthSession();
  const [theme, setThemeState] = useState<Theme>(() => getTheme());
  const locale = useLocale();
  const unreviewedActive = UNREVIEWED.includes(locale);
  const { ago, refresh } = useBackupAge();
  const persisted = usePersistence();

  const aspects = useLocal<Aspect>("aspect");
  const profiles = useLocal<Profile>("profile");
  const sessions = useLocal<PlaySession>("play_session");

  const [message, setMessage] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const backup = backupStatus(ago, sync.pending);
  const liveAspects = aspects.rows.filter((a) => !isArchived(a));
  const guests = profiles.rows.filter((p) => p.is_guest === 1 && !isArchived(p));

  const chooseTheme = (next: Theme): void => {
    setTheme(next);
    setThemeState(next);
  };

  const doExport = async (): Promise<void> => {
    setBusy(true);
    try {
      const blob = await exportBlob();
      const name = backupFilename();
      const file = new File([blob], name, { type: "application/json" });
      // Share sheet where it exists — on a phone that is the difference between
      // a backup in iCloud and a backup in the Downloads folder of a device
      // that is itself the thing at risk.
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Punchboard backup" });
      } else {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = name;
        anchor.click();
        URL.revokeObjectURL(url);
      }
      setMessage({ tone: "ok", text: `Saved ${name}.` });
      refresh();
    } catch (err) {
      // A cancelled share sheet is not a failure worth alarming anyone about.
      if (err instanceof DOMException && err.name === "AbortError") return;
      setMessage({ tone: "danger", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const doImport = async (file: File): Promise<void> => {
    setBusy(true);
    try {
      const restored = await importJson(await file.text());
      setMessage({
        tone: "ok",
        text: `Restored ${restored} rows. Nothing already on this device was overwritten by an older copy.`,
      });
      aspects.reload();
      profiles.reload();
      sessions.reload();
    } catch (err) {
      setMessage({
        tone: "danger",
        text: `That file could not be restored: ${err instanceof Error ? err.message : String(err)}`,
      });
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  return (
    <SettingsScreen
      title="Settings"
      subtitle="The taxonomy, the people, and where your data actually lives."
      back={{ to: "/", label: "Table" }}
    >
      {message && (
        <Banner
          tone={message.tone === "ok" ? "ok" : "danger"}
          title={message.tone === "ok" ? "Done" : "That did not work"}
          actions={
            <button
              type="button"
              className="pb-btn"
              data-variant="quiet"
              onClick={() => setMessage(null)}
            >
              Dismiss
            </button>
          }
        >
          {message.text}
        </Banner>
      )}

      <Section
        title="Backup"
        hint="Browser storage can be cleared without warning, even for an installed app. An export is the copy that survives that."
      >
        <Banner tone={backup.tone === "fresh" ? "ok" : "warn"} title={backup.headline}>
          {backup.detail}
        </Banner>

        <div className="pb-actions">
          <button
            type="button"
            className="pb-btn"
            data-variant="primary"
            disabled={busy}
            onClick={() => void doExport()}
          >
            Export a backup
          </button>
          <button
            type="button"
            className="pb-btn"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            Restore from a file
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            hidden
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              if (file) void doImport(file);
            }}
          />
        </div>

        <Note>
          Restoring merges — rows already on this device are kept, and anything still queued to send
          stays queued. It is safe to restore onto a device that has been in use.
        </Note>

        <p className="pb-quiet">
          {persisted === null
            ? "This browser did not say whether it will keep the app's storage."
            : persisted
              ? "The browser has marked this app's storage as persistent. That is a preference, not a guarantee."
              : "The browser has not marked this app's storage as persistent, so it may be cleared when space runs short."}
        </p>
      </Section>

      <Section title="Your setup">
        {/*
          Plain hash anchors rather than <Link>: Tile carries its state on
          data-attributes, and a component wrapper would swallow them. The hash
          router navigates on `hashchange` either way.
        */}
        <div className="pb-stack">
          <Tile
            as="a"
            href="#/settings/aspects"
            edge="spice"
            title="Aspects"
            meta={`${liveAspects.length} active · ${aspects.rows.length - liveAspects.length} archived`}
          >
            <p className="pb-row__hint">
              What you rate and the words on each stop. Editing changes future sessions only.
            </p>
          </Tile>

          <Tile
            as="a"
            href="#/settings/profiles"
            edge="slate"
            title="Players"
            meta={
              guests.length > 0
                ? `${profiles.rows.length - guests.length} saved · ${guests.length} guest${guests.length === 1 ? "" : "s"} to save`
                : `${profiles.rows.length} saved`
            }
          >
            <p className="pb-row__hint">
              The people at your table, their colours, and how much each of them is asked.
            </p>
          </Tile>

          <Tile
            as="a"
            href="#/settings/sync"
            edge={sync.conflicts > 0 || sync.deadLetters > 0 ? "ochre" : "muted"}
            title="Sync"
            meta={`${sync.pending} unsent · last sync ${sync.lastSyncAt === null ? "never" : formatAgo(Date.now() - sync.lastSyncAt)}`}
          >
            <p className="pb-row__hint">
              {sync.conflicts > 0 || sync.deadLetters > 0
                ? `${sync.conflicts} conflict(s) and ${sync.deadLetters} refused change(s) are waiting on you.`
                : "Nothing is waiting on a decision."}
            </p>
          </Tile>
        </div>
      </Section>

      <Section title="Appearance">
        <div className="pb-field">
          <span className="pb-field__label">Language</span>
          <div className="pb-seg" role="group" aria-label="Language">
            {LANGUAGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                lang={option.value}
                className="pb-seg__opt"
                aria-pressed={locale === option.value}
                onClick={() => setLocale(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <span className="pb-seg__line">
            This device only, and only the questions Punchboard ships with — anything you have
            written yourself stays in the words you wrote it in.
            {unreviewedActive
              ? " This translation has not been checked by a native speaker yet."
              : ""}
          </span>
        </div>

        <div className="pb-field">
          <span className="pb-field__label">Theme</span>
          <div className="pb-seg" role="group" aria-label="Theme">
            {THEMES.map((option) => (
              <button
                key={option.value}
                type="button"
                className="pb-seg__opt"
                aria-pressed={theme === option.value}
                onClick={() => chooseTheme(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <span className="pb-seg__line">
            System follows the phone. The board is dark by default at night, which is when most of
            these sessions happen.
          </span>
        </div>
      </Section>

      {/*
        Below the routine settings, next to the erase: leaving is a considered
        act, not a preference to flick past on the way to the theme. The gate
        means there is always a user here; the guard is for the render that
        happens between a sign-out and the front door taking over.
      */}
      {user && <AccountCard user={user} unsynced={sync.pending} />}

      <Section
        title="Erase this device"
        hint="Only for handing the phone on, or starting clean after a restore."
      >
        <ConfirmButton
          label="Erase local data"
          title="This erases, from this device"
          consequences={wipeConsequences({
            sessions: sessions.rows.length,
            profiles: profiles.rows.length,
            aspects: aspects.rows.length,
            pending: sync.pending,
            conflicts: sync.conflicts,
            deadLetters: sync.deadLetters,
          })}
          confirmLabel="Erase everything local"
          phrase={WIPE_PHRASE}
          variant="danger"
          onConfirm={async () => {
            await wipeLocal();
            aspects.reload();
            profiles.reload();
            sessions.reload();
            setMessage({
              tone: "ok",
              text: "This device is empty. Anything that reached the server comes back on the next sync.",
            });
          }}
        />
        <Note>
          Anything that already reached the server is still there and returns on the next sync.
          Anything that had not is gone. Export first if you are not certain which is which.
        </Note>
      </Section>
    </SettingsScreen>
  );
}
